import React from 'react';
import { Row, Col, InputNumber, Select, Button, Space, Alert } from 'antd';
import { PlayCircleOutlined, EyeOutlined } from '@ant-design/icons';

const monthOptions = Array.from({ length: 12 }, (_, i) => ({
  value: i + 1,
  label: String(i + 1).padStart(2, '0'),
}));

interface ExecuteParamsProps {
  year: number;
  month: number;
  attendStartYear: number;
  attendStartMonth: number;
  attendEndYear: number;
  attendEndMonth: number;
  loading: boolean;
  errors: string[];
  onYearChange: (v: number) => void;
  onMonthChange: (v: number) => void;
  onAttendStartYearChange: (v: number) => void;
  onAttendStartMonthChange: (v: number) => void;
  onAttendEndYearChange: (v: number) => void;
  onAttendEndMonthChange: (v: number) => void;
  onExecute: () => void;
  onPreview: () => void;
}

const ExecuteParamsPanel: React.FC<ExecuteParamsProps> = ({
  year, month,
  attendStartYear, attendStartMonth,
  attendEndYear, attendEndMonth,
  loading, errors,
  onYearChange, onMonthChange,
  onAttendStartYearChange, onAttendStartMonthChange,
  onAttendEndYearChange, onAttendEndMonthChange,
  onExecute, onPreview,
}) => {
  return (
    <div style={{ marginBottom: 16 }}>
      {/* Row 1: 計薪月份 */}
      <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <span style={{ fontSize: 14, fontWeight: 500 }}>計薪月份:</span>
        </Col>
        <Col>
          <InputNumber
            value={year}
            min={2000}
            max={2099}
            onChange={(v) => onYearChange(v ?? 2026)}
            style={{ width: 100 }}
            disabled={loading}
          />
          <span style={{ marginLeft: 4, fontSize: 14 }}>年</span>
        </Col>
        <Col>
          <Select
            value={month}
            options={monthOptions}
            onChange={(v) => onMonthChange(v)}
            style={{ width: 80 }}
            disabled={loading}
          />
          <span style={{ marginLeft: 4, fontSize: 14 }}>月</span>
        </Col>
      </Row>

      {/* Row 2: 考勤月份 (起始) */}
      <Row gutter={16} align="middle" style={{ marginBottom: 8 }}>
        <Col>
          <span style={{ fontSize: 14, fontWeight: 500 }}>考勤月份:</span>
        </Col>
        <Col>
          <InputNumber
            value={attendStartYear}
            min={2000}
            max={2099}
            onChange={(v) => onAttendStartYearChange(v ?? 2026)}
            style={{ width: 100 }}
            disabled={loading}
          />
          <span style={{ marginLeft: 4, fontSize: 14 }}>年</span>
        </Col>
        <Col>
          <Select
            value={attendStartMonth}
            options={monthOptions}
            onChange={(v) => onAttendStartMonthChange(v)}
            style={{ width: 80 }}
            disabled={loading}
          />
          <span style={{ marginLeft: 4, fontSize: 14 }}>月</span>
        </Col>
        <Col>
          <span style={{ fontSize: 14, color: '#666' }}>(起)</span>
        </Col>
      </Row>

      {/* Row 3: 考勤月份 (截止) */}
      <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
        <Col>
          {/* Spacer to align with rows above */}
          <span style={{ fontSize: 14, fontWeight: 500, visibility: 'hidden' }}>考勤月份:</span>
        </Col>
        <Col>
          <InputNumber
            value={attendEndYear}
            min={2000}
            max={2099}
            onChange={(v) => onAttendEndYearChange(v ?? 2026)}
            style={{ width: 100 }}
            disabled={loading}
          />
          <span style={{ marginLeft: 4, fontSize: 14 }}>年</span>
        </Col>
        <Col>
          <Select
            value={attendEndMonth}
            options={monthOptions}
            onChange={(v) => onAttendEndMonthChange(v)}
            style={{ width: 80 }}
            disabled={loading}
          />
          <span style={{ marginLeft: 4, fontSize: 14 }}>月</span>
        </Col>
        <Col>
          <span style={{ fontSize: 14, color: '#666' }}>(迄)</span>
        </Col>
      </Row>

      {/* Buttons: matching FoxPro: 說明 / P開始過帳 / X離開 */}
      <Row>
        <Col>
          <Space>
            <Button
              type="primary"
              icon={<PlayCircleOutlined />}
              onClick={onExecute}
              loading={loading}
              danger
            >
              P開始過帳
            </Button>
            <Button
              icon={<EyeOutlined />}
              onClick={onPreview}
              loading={loading}
            >
              預覽
            </Button>
          </Space>
        </Col>
      </Row>
      {errors.length > 0 && (
        <div style={{ marginTop: 12 }}>
          {errors.map((err, i) => (
            <Alert key={i} message={err} type="error" showIcon style={{ marginBottom: 4 }} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ExecuteParamsPanel;
